import React, { useRef, useState } from 'react';
import { DashboardHeader } from '../components/DashboardHeader';
import { ExportingIndicator } from '../components/ExportingIndicator';
import { LoanBreakdownTable } from '../components/LoanBreakdownTable';
import { StatCard } from '../components/StatCard';
import { TierLadder } from '../components/TierLadder';
import { TierStatusCard } from '../components/TierStatusCard';
import { UnitBonusTracker } from '../components/UnitBonusTracker';
import { periods, producer, tiers } from '../data/commissions';
import { activeStep, commissionFor, countUnits, formatCurrency, minBonusUnits, sumVolume } from '../utils/commission';
import { exportElementToPdf } from '../utils/pdf';
export function CommissionsDashboard() {
  const [selectedPeriodId, setSelectedPeriodId] = useState(periods[0].id);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const statementRef = useRef<HTMLDivElement>(null);
  const period = periods.find(item => item.id === selectedPeriodId) ?? periods[0];
  const tier = tiers.find(item => item.id === period.tierId) ?? tiers[0];
  const nextTier = tiers.find(item => item.level === tier.level + 1) ?? null;
  const fundedLoans = period.loans.filter(loan => loan.countsAsUnit);
  const pipelineLoans = period.loans.filter(loan => !loan.countsAsUnit);
  const units = countUnits(period.loans);
  const fundedVolume = sumVolume(fundedLoans);
  const pipelineVolume = sumVolume(pipelineLoans);
  const step = activeStep(tier, units);
  const minUnits = minBonusUnits(tier);
  const scorecardMet = period.scorecard.points >= period.scorecard.goal;
  const eligible = scorecardMet && units >= minUnits;
  const earnedBps = eligible ? step.bps : tier.baseBps;
  const bonusBps = earnedBps - tier.baseBps;
  const earnedCommission = fundedLoans.reduce((total, loan) => total + commissionFor(loan.amount, earnedBps), 0);
  const paidOut = fundedLoans.filter(loan => loan.status === 'paid').reduce((total, loan) => total + commissionFor(loan.amount, earnedBps), 0);
  const projectedCommission = earnedCommission + pipelineLoans.reduce((total, loan) => total + commissionFor(loan.amount, step.bps), 0);
  async function handleExport() {
    const node = statementRef.current;
    if (!node || isExporting) return;
    setExportError(null);
    setIsExporting(true);
    try {
      // Let the export-only header state paint before capturing.
      await new Promise(resolve => window.setTimeout(resolve, 80));
      await exportElementToPdf(node, `commission-statement-${period.id}.pdf`);
    } catch {
      setExportError('We couldn’t generate the PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }
  return <div className="min-h-screen w-full bg-slate-50" data-id="element-146" data-mp="1oz18m5gtzjhf">
      <div ref={statementRef} className="bg-slate-50" data-id="element-147" data-mp="1k2auhnol6xyn">
      <DashboardHeader producer={producer} periods={periods} selectedPeriodId={period.id} onSelectPeriod={setSelectedPeriodId} onExport={handleExport} isExporting={isExporting} data-id="element-148" data-mp="1k3vp1dawigwi" />

      <main className="mx-auto max-w-7xl space-y-6 px-6 py-8" data-id="element-149" data-mp="14yrkw60kux26">
        {exportError ? <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-id="element-150" data-mp="19wt2emicuobj">
            {exportError}
          </p> : null}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-151" data-mp="1jwtmwbrynahb">
          <StatCard label={period.isCurrent ? 'Earned month to date' : 'Commission earned'} value={formatCurrency(earnedCommission)} helper={`${formatCurrency(paidOut)} paid out`} emphasis data-id="element-152" data-mp="1jtxrau3l4xw1" />
          <StatCard label="Funded units" value={String(units)} helper={`${formatCurrency(fundedVolume)} volume`} data-id="element-153" data-mp="r5ib448pw9j4" />
          <StatCard label="Earning rate" value={`${earnedBps} BPS`} helper={bonusBps > 0 ? `${tier.baseBps} base + ${bonusBps} unit bonus` : `${tier.baseBps} base · no bonus`} data-id="element-154" data-mp="10xfsjsqbjxqa" />
          <StatCard label={period.isCurrent ? 'Projected month end' : 'Final month total'} value={formatCurrency(projectedCommission)} helper={period.isCurrent ? `${pipelineLoans.length} in pipeline · ${formatCurrency(pipelineVolume)}` : 'Statement closed'} data-id="element-155" data-mp="3fxsxnke1gaw" />
        </div>

        <div className="grid items-stretch gap-6 xl:grid-cols-3" data-id="element-156" data-mp="258ixr1dpyz56">
          <TierStatusCard tier={tier} nextTier={nextTier} totalTiers={tiers.length} tenureMonths={period.seniorTenureMonths} rollingUnits={period.rollingSixMonthUnits} data-id="element-157" data-mp="2jwm44bvoccui" />
          <div className="xl:col-span-2" data-id="element-158" data-mp="2f3kmvx7zw17r">
            <UnitBonusTracker tier={tier} units={units} volume={fundedVolume} scorecard={period.scorecard} eligible={eligible} isCurrent={period.isCurrent} data-id="element-159" data-mp="2svr0pcnodw41" />
          </div>
        </div>

        <LoanBreakdownTable key={period.id} loans={fundedLoans} earnedBps={earnedBps} periodLabel={period.label} hideSearch={isExporting} data-id="element-160" data-mp="32v841nfgfxwu" />

        <TierLadder tiers={tiers} currentTierLevel={tier.level} data-id="element-161" data-mp="2xwofrgde2a1b" />
      </main>
      </div>

      {isExporting ? <ExportingIndicator label={period.label} data-id="element-162" data-mp="18j1gdp5djonn" /> : null}
    </div>;
}